// React
import { useContext, useState } from 'react'
// 3rd Party
import { twMerge } from 'tailwind-merge'
// MainStem - UI
import { theme as MSTheme } from '@mainstem/react-mainstem'
// Icons
import { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faArrowRight,
  faArrowRotateLeft,
  faCalendar,
  faEnvelope,
  faGrid2,
  faMagnifyingGlass,
  faMessage,
  faMessageLines,
  faSidebar,
  faSlidersUp
} from '@fortawesome/pro-light-svg-icons'
// Global - Context
import { AppContext } from 'context'
// Global - UI
import { Button, Modal, ModalBody, ModalFooter } from 'components/ui'
// Layout - Helpers
import { getBetaAppUrl } from '../nav-top/buttons/getBetaAppUrl'
// Local - Helpers
import { hasDeclinedBetaInvite, rememberBetaInviteDeclined } from './cookie'

interface Feature {
  description: string
  icon: IconDefinition
  label: string
  title: string
}

const features: Feature[] = [
  {
    description: 'Find products, suppliers, orders, invoices and more from one search bar.',
    icon: faMagnifyingGlass,
    label: 'Omni-Search',
    title: 'Search everything'
  },
  {
    description: 'One-click shortcuts to what you use most, plus alerts when something needs you.',
    icon: faGrid2,
    label: 'Home Page',
    title: 'A smarter home base'
  },
  {
    description: "Open any order, product or supplier in a drawer. Close it, and you're right back.",
    icon: faSidebar,
    label: 'Drawers',
    title: 'Never lose your place'
  },
  {
    description: "Pick a theme in your Profile. Sort, filter and export any table, and we'll remember it.",
    icon: faSlidersUp,
    label: 'Themes & Tables',
    title: 'MainStem, your way'
  }
]

const pillClassName =
  'inline-flex items-center gap-1.5 rounded-full border-[1px] border-slate-200 bg-white px-3.5 py-1 text-xs font-medium text-slate-700'

export const BetaInvite = () => {
  const { loggedInUser, userToken } = useContext(AppContext)
  const [dismissed, setDismissed] = useState(false)

  const betaUrl = loggedInUser?.allowBeta && userToken ? getBetaAppUrl(window.location.hostname, userToken) : null
  const declined = loggedInUser?.uuid ? hasDeclinedBetaInvite(loggedInUser.uuid) : false
  const open = Boolean(betaUrl) && !declined && !dismissed

  const handleDecline = () => {
    if (loggedInUser?.uuid) {
      rememberBetaInviteDeclined(loggedInUser.uuid)
    }
    setDismissed(true)
  }

  const handleAccept = () => {
    if (!betaUrl) {
      return
    }
    setDismissed(true)
    window.location.assign(betaUrl)
  }

  if (!open) {
    return null
  }

  return (
    <Modal
      className="flex max-h-[90vh] flex-col overflow-y-auto font-['Poppins',sans-serif] md:flex-row md:overflow-hidden"
      size='xl'
    >
      <div className='relative flex shrink-0 flex-col gap-8 overflow-hidden bg-gradient-to-br from-violet-800 to-violet-900 px-7 py-8 text-white md:w-[310px]'>
        <div
          aria-hidden
          className='pointer-events-none absolute -right-4 top-8 h-20 w-20 rotate-12 rounded-2xl border-2 border-white/10'
        />
        <div
          aria-hidden
          className='pointer-events-none absolute -right-28 top-[42%] h-64 w-64 rounded-full border-[1px] border-white/10'
        />
        <div
          aria-hidden
          className='pointer-events-none absolute -right-16 top-[calc(42%+4rem)] h-32 w-32 rounded-full border-[1px] border-white/10'
        />
        <div
          aria-hidden
          className='pointer-events-none absolute -bottom-6 left-6 h-px w-48 origin-left -rotate-[60deg] bg-white/10'
        />

        <div className='relative flex flex-col gap-6'>
          <img alt='MainStem' className='h-7 w-auto self-start' src={MSTheme.logos.mainstem.white} />
          <span className='inline-flex items-center gap-2 self-start rounded-full border-[1px] border-white/20 bg-white/10 px-3.5 py-1 text-[10px] font-semibold uppercase tracking-[0.15em]'>
            <span className='h-1.5 w-1.5 rounded-full bg-teal-300' />
            Now available
          </span>
          <h2 className='m-0 flex flex-col font-semibold leading-none text-white'>
            <span className='text-2xl font-medium'>Welcome to</span>
            <span className='mt-2 text-4xl font-bold'>MainStem</span>
            <span className='text-8xl font-bold tracking-tight text-lime-300'>2.0</span>
          </h2>
          <p className='m-0 text-sm leading-relaxed text-white/85'>
            A fresh new look, built to make buying faster and easier. Cleaner, AI-powered and simpler to navigate, it's
            ready for you today, ahead of some of our biggest features yet, coming in 2027.
          </p>
        </div>

        <div className='relative my-auto flex gap-3 rounded-xl border-[1px] border-white/10 bg-violet-950/40 p-[1rem]'>
          <div className='flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] bg-lime-300 text-violet-900'>
            <FontAwesomeIcon icon={faCalendar} />
          </div>
          <div className='flex flex-col gap-1'>
            <span className='text-[10px] font-semibold uppercase tracking-[0.15em] text-lime-300'>
              Mark your calendar
            </span>
            <span className='whitespace-nowrap text-base font-bold leading-tight'>November 13, 2026</span>
            <span className='text-xs leading-snug text-white/75'>
              Our tentative full-switch date. After that, MainStem 2.0 becomes the only version.
            </span>
          </div>
        </div>

        <div className='relative flex flex-col text-xs text-white/80'>
          <span>Thank you for your support,</span>
          <span className='mt-1 text-base font-bold text-white'>John Wesley</span>
          <span>Chief Product Officer, MainStem</span>
        </div>
      </div>

      <div className='flex min-h-0 shrink-0 flex-col md:flex-1 md:shrink'>
        <ModalBody className='flex min-h-0 flex-1 flex-col gap-5 px-6 py-6 md:overflow-y-auto sm:px-8'>
          <span className='text-xs font-bold uppercase tracking-[0.15em] text-violet-700'>What's new in 2.0</span>

          <div className='grid grid-cols-1 gap-3 sm:grid-cols-2'>
            {features.map((feature) => (
              <div
                className='flex flex-col gap-1 rounded-xl border-[1px] border-violet-100 bg-violet-50/40 p-[1rem]'
                key={feature.label}
              >
                <div className='mb-2.5 flex h-9 w-9 items-center justify-center rounded-[10px] bg-violet-800 text-white'>
                  <FontAwesomeIcon icon={feature.icon} />
                </div>
                <span className='text-[10px] font-semibold uppercase tracking-[0.15em] text-violet-700'>
                  {feature.label}
                </span>
                <span className='text-base font-semibold text-slate-900'>{feature.title}</span>
                <span className='text-xs leading-relaxed text-slate-600'>{feature.description}</span>
              </div>
            ))}
          </div>

          <div className='flex items-center gap-3 rounded-xl bg-cyan-50 p-3.5'>
            <div className='flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-cyan-400 text-cyan-950'>
              <FontAwesomeIcon icon={faArrowRotateLeft} />
            </div>
            <span className='text-xs leading-relaxed text-slate-700'>
              <strong className='text-slate-900'>Still getting comfortable?</strong> Use{' '}
              <strong className='text-slate-900'>Switch to MainStem 1.0</strong> at the bottom of any screen, and the{' '}
              <strong className='text-slate-900'>MainStem 2.0</strong> button up top to return.
            </span>
          </div>

          <div className='flex flex-col gap-3'>
            <span className='text-xs leading-relaxed text-slate-600'>
              <strong className='text-slate-900'>MainStem is built for you.</strong> Something not quite right, got an
              idea, or just not a fan? We want to hear it.
            </span>
            <div className='flex flex-wrap gap-2'>
              <span className={pillClassName}>
                <FontAwesomeIcon className='text-violet-700' icon={faMessage} />
                In-app chat
              </span>
              <span className={pillClassName}>
                <FontAwesomeIcon className='text-violet-700' icon={faMessageLines} />
                The “2.0 Feedback” button
              </span>
              <a
                className={twMerge(
                  pillClassName,
                  'border-violet-200 bg-violet-50 text-violet-800! no-underline! transition-colors hover:bg-violet-100 hover:text-violet-900!'
                )}
                href='mailto:feedback@mainstem.io'
              >
                <FontAwesomeIcon icon={faEnvelope} />
                feedback@mainstem.io
              </a>
            </div>
          </div>
        </ModalBody>

        <ModalFooter className='gap-3 border-slate-200 bg-white px-6 py-[1rem] sm:px-8'>
          <Button
            className='rounded-xl! border-[1px] border-slate-300 bg-[#fff] px-[1.25rem] py-2.5 font-semibold text-slate-800 hover:bg-slate-50'
            color='secondary'
            onClick={handleDecline}
            type='button'
          >
            Not yet
          </Button>
          <Button
            className='rounded-xl! bg-violet-800 px-[1.25rem] py-2.5 font-semibold shadow-violet-200 hover:bg-violet-900 focus:ring-violet-500 active:bg-violet-950'
            icon={faArrowRight}
            iconPosition='right'
            onClick={handleAccept}
            type='button'
          >
            Launch MainStem 2.0
          </Button>
        </ModalFooter>
      </div>
    </Modal>
  )
}
